import { __assign, __read, __spread } from "tslib";
import React, { useMemo, useRef, useState, useLayoutEffect, useCallback, useEffect, } from 'react';
import { useRowSelect, useBlockLayout, usePagination, useSortBy, useTable, } from 'react-table';
import Measure from 'react-measure';
import { useInView } from 'react-intersection-observer';
import { AnimatePresence } from 'framer-motion';
import { transparentize } from 'polished';
import { useScrollOverflowHandler } from '@keen.io/react-hooks';
import { copyToClipboard } from '@keen.io/charts-utils';
import { TableFooter, PER_PAGE_OPTIONS } from '@keen.io/ui-core';
import { colors } from '@keen.io/colors';
import { BodyText } from '@keen.io/typography';
import { theme as defaultTheme } from '../../theme';
import { LeftOverflow, RightOverflow, TableContainer, TableScrollWrapper, StyledCol, StyledTable, TableFooterContainer, TotalRows, } from './table-chart.styles';
import { Body, Header, CopyCellTooltip, SelectedRowsInfo, SelectedRowsCopiedInfo, } from './components';
import { generateSelectedRowsCSVData, generateHeader, generateTable, setColumnsOrder, sortData, } from './utils';
import { useTableEvents, useRowsGroupSelection } from './hooks';
import { SELECT_COLUMN_ID } from './constants';
export var TOOLTIP_MOTION = {
    transition: { duration: 0.3 },
    exit: { opacity: 0 },
};
export var TableChart = function (_a) {
    var tableData = _a.data, _b = _a.theme, theme = _b === void 0 ? defaultTheme : _b, columnsOrder = _a.columnsOrder, formatValue = _a.formatValue, _c = _a.enableEditMode, enableEditMode = _c === void 0 ? false : _c, _d = _a.rowsSelection, rowsSelection = _d === void 0 ? false : _d, chartEvents = _a.chartEvents, _f = _a.columnsNamesMapping, columnsNamesMapping = _f === void 0 ? {} : _f, _g = _a.pagination, pagination = _g === void 0 ? true : _g, _h = _a.rowsPerPage, rowsPerPage = _h === void 0 ? PER_PAGE_OPTIONS[3] : _h;
    var tableRef = useRef(null);
    var containerRef = useRef(null);
    var componentMounted = useRef(false);
    var _j = __read(useState(null), 2), sort = _j[0], setSort = _j[1];
    var _k = __read(useState([]), 2), columnsWidth = _k[0], setColumnsWidth = _k[1];
    var _l = __read(useState(false), 2), selectedRowsCopied = _l[0], setSelectedRowsCopied = _l[1];
    var _m = useRowsGroupSelection({ enabled: rowsSelection }), setSelectionOffset = _m.setSelectionOffset, selectionOffset = _m.selectionOffset, getSelectionOffsetRange = _m.getSelectionOffsetRange;
    var _o = __read(useState({
        selectors: null,
        visible: false,
        x: 0,
        y: 0,
    }), 2), tooltip = _o[0], setTooltip = _o[1];
    var _p = __read(useState(), 2), hoveredColumn = _p[0], setHoveredColumn = _p[1];
    var _q = __read(useState([]), 2), selectedColumns = _q[0], setSelectedColumns = _q[1];
    var _r = __read(useState(0), 2), footerHeight = _r[0], setFooterHeight = _r[1];
    var publishColumnSelection = useTableEvents({
        chartEvents: chartEvents,
        onDeselectColumns: function () { return setSelectedColumns([]); },
    }).publishColumnSelection;
    var _s = __read(useInView(), 2), inViewRef = _s[0], inView = _s[1];
    var reduceColumnsSelection = useCallback(function (columnName, columnIndex) {
        var index = selectedColumns.findIndex(function (_a) {
            var index = _a.index;
            return columnIndex === index;
        });
        var arr = __spread(selectedColumns);
        if (index > -1) {
            arr.splice(index, 1);
        }
        else {
            arr.push({ columnName: columnName, index: columnIndex });
        }
        return arr;
    }, [selectedColumns]);
    var _t = theme.table, header = _t.header, body = _t.body, mainColor = _t.mainColor, tooltipSettings = theme.tooltip;
    var data = useMemo(function () {
        var sortColumns = tableData.length && (columnsOrder === null || columnsOrder === void 0 ? void 0 : columnsOrder.length);
        return sortColumns ? setColumnsOrder(columnsOrder, tableData) : tableData;
    }, [columnsOrder, tableData]);
    var sortedData = sort ? sortData(__spread(data), sort, formatValue) : data;
    var formattedData = React.useMemo(function () { return generateTable(sortedData, formatValue); }, [sort, formatValue]);
    var columns = React.useMemo(function () { return generateHeader(formattedData[0]); }, [
        columnsOrder,
        tableData,
    ]);
    var indexesOfSelectedColumns = selectedColumns.map(function (_a) {
        var index = _a.index;
        return index;
    });
    var activeColumns = new Set(__spread(indexesOfSelectedColumns, [hoveredColumn]).filter(function (i) { return typeof i === 'number'; }));
    var itemsPerPage = pagination ? rowsPerPage : data.length;
    var _u = useTable({
        columns: columns,
        data: formattedData,
        initialState: {
            pageIndex: 0,
            pageSize: itemsPerPage,
            hiddenColumns: rowsSelection ? [] : [SELECT_COLUMN_ID],
        },
        manualSortBy: true,
        disableMultiSort: true,
    }, useBlockLayout, useSortBy, usePagination, useRowSelect), getTableProps = _u.getTableProps, getTableBodyProps = _u.getTableBodyProps, headerGroups = _u.headerGroups, prepareRow = _u.prepareRow, page = _u.page, pageCount = _u.pageCount, gotoPage = _u.gotoPage, setPageSize = _u.setPageSize, toggleRowSelected = _u.toggleRowSelected, toggleHideColumn = _u.toggleHideColumn, toggleAllRowsSelected = _u.toggleAllRowsSelected, _v = _u.state, pageIndex = _v.pageIndex, pageSize = _v.pageSize, sortBy = _v.sortBy, selectedRowIds = _v.selectedRowIds;
    useEffect(function () {
        setPageSize(itemsPerPage);
    }, [rowsPerPage, pagination, itemsPerPage, setPageSize]);
    useEffect(function () {
        if (sortBy && sortBy.length > 0) {
            setSort({
                property: sortBy[0].id,
                sort: sortBy[0].desc ? 'descending' : 'ascending',
            });
        }
        else {
            setSort(null);
        }
    }, [sortBy]);
    var _w = useScrollOverflowHandler(containerRef), overflowRight = _w.overflowRight, overflowLeft = _w.overflowLeft, scrollHandler = _w.scrollHandler;
    var tooltipHide = useRef(null);
    var onRowSelect = function (e, rowId) {
        if (e.shiftKey) {
            if (selectionOffset) {
                var selectionRange = getSelectionOffsetRange(rowId);
                if (selectionRange)
                    selectionRange.forEach(function (rowId) {
                        return toggleRowSelected(rowId, true);
                    });
            }
            else {
                setSelectionOffset(rowId);
            }
        }
        else {
            toggleRowSelected(rowId);
        }
    };
    var onCellClick = function (e, _a) {
        var columnName = _a.columnName, columnType = _a.columnType, rowId = _a.rowId, value = _a.value, idx = _a.idx;
        if (columnType === 'row-selection' && !enableEditMode) {
            onRowSelect(e, rowId);
        }
        else if (columnType === 'value') {
            if (enableEditMode) {
                var columns_1 = reduceColumnsSelection(columnName, idx);
                publishColumnSelection(data, formatValue, columns_1);
                setSelectedColumns(columns_1);
            }
            else {
                if (tooltipHide.current)
                    clearTimeout(tooltipHide.current);
                copyToClipboard(value);
                var pageX_1 = e.pageX, pageY_1 = e.pageY;
                setTooltip(function (state) { return (__assign(__assign({}, state), { visible: true, x: pageX_1, y: pageY_1 })); });
                tooltipHide.current = setTimeout(function () {
                    setTooltip(function (state) { return (__assign(__assign({}, state), { visible: false, x: 0, y: 0 })); });
                }, 1500);
            }
        }
    };
    useEffect(function () {
        if (componentMounted.current) {
            setColumnsWidth([]);
            toggleHideColumn(SELECT_COLUMN_ID, !rowsSelection);
        }
        else {
            componentMounted.current = true;
        }
    }, [rowsSelection]);
    useLayoutEffect(function () {
        if (containerRef.current && columnsWidth.length === 0) {
            var headersWidth = Array.from(containerRef.current.querySelectorAll('th')).map(function (header, index) {
                if (index === 0 && rowsSelection)
                    return 'auto';
                return header.offsetWidth;
            });
            setColumnsWidth(headersWidth);
        }
    }, [rowsSelection, columnsWidth]);
    var selectedRowsIds = Object.keys(selectedRowIds);
    var copySelectedRows = function () {
        var selectedRows = selectedRowsIds.map(function (selectedRowId) { return formattedData[selectedRowId]; });
        var columnsKeys = Object.keys(formattedData[0]);
        var csvData = generateSelectedRowsCSVData({
            selectedRows: selectedRows,
            columnsKeys: columnsKeys,
            columnsNamesMapping: columnsNamesMapping,
            addColumnNames: selectedRowsIds.length === formattedData.length,
        });
        copyToClipboard(csvData);
        toggleAllRowsSelected(false);
        setSelectedRowsCopied(true);
    };
    useEffect(function () {
        var timer = setTimeout(function () { return setSelectedRowsCopied(false); }, 2000);
        return function () { return clearTimeout(timer); };
    }, [selectedRowsCopied]);
    return (React.createElement(TableScrollWrapper, null,
        React.createElement(TableContainer, { ref: containerRef, onScroll: scrollHandler, footerHeight: footerHeight, isOverflow: !inView, "data-testid": "table-chart-plot" },
            React.createElement(CopyCellTooltip, { tooltipState: tooltip, tooltipSettings: tooltipSettings }),
            React.createElement(AnimatePresence, null, selectedRowsIds.length > 0 && (React.createElement(SelectedRowsInfo, { selectedRowsNumber: selectedRowsIds.length, onClearRowsSelection: function () { return toggleAllRowsSelected(false); }, onCopySelectedRows: function () { return copySelectedRows(); } }))),
            React.createElement(AnimatePresence, null, selectedRowsCopied && React.createElement(SelectedRowsCopiedInfo, null)),
            React.createElement(StyledTable, __assign({}, getTableProps(), { ref: tableRef }),
                React.createElement("colgroup", null, headerGroups[0].headers.map(function (item, idx) { return (React.createElement(StyledCol, { key: "col-" + item.Header + "-" + idx, isHovered: hoveredColumn === idx, isSelected: indexesOfSelectedColumns.includes(idx) })); })),
                React.createElement(Header, __assign({ headerGroups: headerGroups, columnsNamesMapping: columnsNamesMapping, typography: header.typography, color: mainColor, activeColumns: __spread(activeColumns), editMode: enableEditMode }, (enableEditMode &&
                    publishColumnSelection && {
                    onEditModeClick: function (_e, columnName, cellIdx) {
                        var selectedColumns = reduceColumnsSelection(columnName, cellIdx);
                        publishColumnSelection(data, formatValue, selectedColumns);
                        setSelectedColumns(selectedColumns);
                    },
                    onCellMouseEnter: function (_e, cellIdx) { return setHoveredColumn(cellIdx); },
                    onCellMouseLeave: function () { return setHoveredColumn(null); },
                }))),
                React.createElement(Body, __assign({ page: page, getTableBodyProps: getTableBodyProps, onCellClick: function (e, meta) { return onCellClick(e, meta); }, prepareRow: prepareRow, backgroundColor: mainColor, typography: body.typography, isEditMode: enableEditMode, columnsWidth: columnsWidth, disableValuesSelection: !!selectionOffset, activeColumns: __spread(activeColumns) }, (enableEditMode && {
                    onCellMouseEnter: function (_e, cellIdx) { return setHoveredColumn(cellIdx); },
                    onCellMouseLeave: function () { return setHoveredColumn(null); },
                })))),
            React.createElement("div", { ref: inViewRef }),
            overflowLeft && React.createElement(LeftOverflow, null),
            overflowRight && React.createElement(RightOverflow, null)),
        React.createElement(Measure, { bounds: true, onResize: function (_a) {
                var height = _a.bounds.height;
                return setFooterHeight(height);
            } }, function (_a) {
            var measureRef = _a.measureRef;
            return (React.createElement(TableFooterContainer, { ref: measureRef }, pagination ? (React.createElement(TableFooter, { rows: data.length, page: pageIndex + 1, totalPages: pageCount, itemsPerPage: pageSize, onPageChange: function (page) {
                    containerRef.current.scrollTop = 0;
                    gotoPage(page - 1);
                }, onItemsPerPageChange: function (pageSize) {
                    containerRef.current.scrollTop = 0;
                    setPageSize(pageSize);
                    gotoPage(0);
                } })) : (React.createElement(TotalRows, null,
                React.createElement(BodyText, { variant: "body2", color: transparentize(0.5, colors.black[500]), enableTextEllipsis: true },
                    data.length.toLocaleString('en-US'),
                    " rows")))));
        })));
};
export default TableChart;
